/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import get from 'lodash/get';
import sortBy from 'lodash/sortBy';
import update from 'immutability-helper';

import {
  GET_MONITOR_ASSET_TYPES,
  GET_MONITOR_ASSET_TYPES_FAILURE,
  GET_MONITOR_ASSET_TYPES_SUCCESS,
  GET_MONITOR_DASHBOARDS,
  GET_MONITOR_DASHBOARDS_FAILURE,
  GET_MONITOR_DASHBOARDS_SUCCESS,
  GET_MONITOR_HIERARCHY_DASHBOARD,
  GET_MONITOR_HIERARCHY_DASHBOARD_FAILURE,
  GET_MONITOR_HIERARCHY_DASHBOARD_SUCCESS,
  PERSIST_HIERARCHY_LIST,
  CLEAN_PERSIST_HIERARCHY_LIST,
  GET_MONITOR_ASSETS,
  GET_MONITOR_ASSETS_FAILURE,
  GET_MONITOR_ASSETS_SUCCESS,
  GET_MONITOR_LOCATIONS,
  GET_MONITOR_LOCATIONS_FAILURE,
  GET_MONITOR_LOCATIONS_SUCCESS,
} from '../actions/monitorActionCreators';
import {
  DELETE_DASHBOARD_TEMPLATE_SUCCESS,
  UPDATE_DASHBOARD_TEMPLATE_SUCCESS,
} from '../../Dashboard/actions/monitoringActionCreators';

export const monitorInitialState = {
  assetTypes: {
    data: [],
    isFetchingAssetTypes: false,
    fetchDataError: null,
  },
  assets: {
    data: [],
    isFetchingAssets: false,
    fetchDataError: null,
  },
  dashboards: {
    data: [],
    isFetchingDashboards: false,
    fetchDataError: null,
  },
  hierarchyDashboards: {},
  hierarchyListState: {
    listData: null,
    expandedIds: null,
  },
  assetsData: {
    data: [],
    isFetchingAssets: false,
    fetchDataError: null,
  },
  locations: {
    data: [],
    isFetchingLocations: false,
    fetchDataError: null,
  },
};

export const monitorReducer = (state = monitorInitialState, action) => {
  switch (action.type) {
    // Get monitor asset types
    case GET_MONITOR_ASSET_TYPES:
      return update(state, {
        assetTypes: {
          isFetchingAssetTypes: { $set: true },
          fetchDataError: { $set: null },
        },
      });
    case GET_MONITOR_ASSET_TYPES_FAILURE:
      return update(state, {
        assetTypes: {
          isFetchingAssetTypes: { $set: false },
          fetchDataError: { $set: action.payload },
        },
      });
    case GET_MONITOR_ASSET_TYPES_SUCCESS:
      return update(state, {
        assetTypes: {
          fetchDataError: { $set: null },
          isFetchingAssetTypes: { $set: false },
          data: { $set: action.payload },
        },
      });
    // Get monitor dashboards
    case GET_MONITOR_DASHBOARDS:
      return update(state, {
        dashboards: {
          isFetchingDashboards: { $set: true },
          fetchDataError: { $set: null },
        },
      });
    case GET_MONITOR_DASHBOARDS_FAILURE:
      return update(state, {
        dashboards: {
          isFetchingDashboards: { $set: false },
          fetchDataError: { $set: action.payload },
        },
      });
    case GET_MONITOR_DASHBOARDS_SUCCESS:
      return update(state, {
        dashboards: {
          fetchDataError: { $set: null },
          isFetchingDashboards: { $set: false },
          data: { $set: action.payload },
        },
      });
    case UPDATE_DASHBOARD_TEMPLATE_SUCCESS: {
      const dashboardToUpdate = state.dashboards?.data?.findIndex(
        dashboard => action.payload.dashboardMetaId === dashboard.dashboardMetaId
      );
      if (dashboardToUpdate !== -1) {
        return update(state, {
          dashboards: {
            fetchDataError: { $set: null },
            isFetchingDashboards: { $set: false },
            data: { $splice: [[dashboardToUpdate, 1, action.payload]] },
          },
        });
      }
      return state;
    }
    case DELETE_DASHBOARD_TEMPLATE_SUCCESS: {
      const dashboardToRemove = state.dashboards?.data?.findIndex(
        dashboard => action.payload.dashboardMetaId === dashboard.dashboardMetaId
      );
      if (dashboardToRemove !== -1) {
        return update(state, {
          dashboards: {
            fetchDataError: { $set: null },
            isFetchingDashboards: { $set: false },
            data: { $splice: [[dashboardToRemove, 1]] },
          },
        });
      }
      return state;
    }
    // HIERARCHY DASHBOARDS
    case GET_MONITOR_HIERARCHY_DASHBOARD: {
      const { hierarchyType, hierarchyId } = action;
      return {
        ...state,
        hierarchyDashboards: {
          ...state?.hierarchyDashboards,
          [hierarchyId]: {
            hierarchyType,
            data: [],
            isFetchingDashboards: true,
            fetchDataError: null,
          },
        },
      };
    }
    case GET_MONITOR_HIERARCHY_DASHBOARD_FAILURE: {
      const { hierarchyType, hierarchyId, payload } = action;
      return {
        ...state,
        hierarchyDashboards: {
          ...state?.hierarchyDashboards,
          [hierarchyId]: {
            hierarchyType,
            data: [],
            isFetchingDashboards: false,
            fetchDataError: payload,
          },
        },
      };
    }
    case GET_MONITOR_HIERARCHY_DASHBOARD_SUCCESS: {
      const { hierarchyType, hierarchyId, payload } = action;
      return {
        ...state,
        hierarchyDashboards: {
          ...state?.hierarchyDashboards,

          [hierarchyId]: {
            hierarchyType,
            data: payload,
            isFetchingDashboards: false,
            fetchDataError: null,
          },
        },
      };
    }
    case PERSIST_HIERARCHY_LIST: {
      const { listData, expandedId, expanded } = action.payload;
      return {
        ...state,
        hierarchyListState: {
          listData: listData ?? state?.hierarchyListState?.listData,
          expandedIds: expanded
            ? [...(state?.hierarchyListState?.expandedIds ?? []), expandedId]
            : state?.hierarchyListState?.expandedIds?.filter(i => i !== expandedId) ?? [],
        },
      };
    }
    case CLEAN_PERSIST_HIERARCHY_LIST: {
      return {
        ...state,
        hierarchyListState: { listData: null, expandedIds: null },
      };
    }
    case GET_MONITOR_ASSETS:
      return update(state, {
        assetsData: {
          isFetchingAssets: { $set: true },
          fetchDataError: { $set: null },
        },
      });
    case GET_MONITOR_ASSETS_FAILURE:
      return update(state, {
        assetsData: {
          isFetchingAssets: { $set: false },
          fetchDataError: { $set: action.payload },
        },
      });
    case GET_MONITOR_ASSETS_SUCCESS:
      return {
        ...state,
        assetsData: {
          fetchDataError: null,
          isFetchingAssets: false,
          data: [
            // Make sure we first remove existing state resources that are included in the payload, so that this state update becomes a union operation
            ...state.assetsData.data.filter(
              asset => !action.payload.find(result => result.uuid === asset.uuid)
            ),
            // Include the resources from the payload, they won't create duplicates as all resources with matching uuId have been filtered out
            ...action.payload,
          ],
        },
      };

    case GET_MONITOR_LOCATIONS:
      return update(state, {
        locations: {
          isFetchingLocations: { $set: true },
          fetchDataError: { $set: null },
        },
      });
    case GET_MONITOR_LOCATIONS_FAILURE:
      return update(state, {
        locations: {
          isFetchingLocations: { $set: false },
          fetchDataError: { $set: action.payload },
        },
      });

    case GET_MONITOR_LOCATIONS_SUCCESS:
      return {
        ...state,
        locations: {
          fetchDataError: null,
          isFetchingLocations: false,
          data: [
            // Make sure we first remove existing state resources that are included in the payload, so that this state update becomes a union operation
            ...state.locations.data.filter(
              location => !action.payload.find(result => result.uuid === location.uuid)
            ),
            // Include the resources from the payload, they won't create duplicates as all resources with matching uuId have been filtered out
            ...action.payload,
          ],
        },
      };

    default:
      return state;
  }
};

// Selectors
// TODO: can we reduce or remove this next 3 selectors ?
export const getMonitorAssetTypes = state => get(state, 'monitor.assetTypes.data');
export const getMonitorAssetTypesFetchingData = state =>
  get(state, 'monitor.assetTypes.isFetchingAssetTypes');
export const getMonitorAssetTypesFetchingDataError = state =>
  get(state, 'monitor.assetTypes.fetchDataError');

export const getMonitorAssets = state => get(state, 'monitor.assets.data');
export const getMonitorAssetsFetchingData = state => get(state, 'monitor.assets.isFetchingAssets');
export const getMonitorAssetsFetchingDataError = state =>
  get(state, 'monitor.assets.fetchDataError');

// TODO: memoize this selector
export const getMonitorDashboards = state =>
  sortBy(get(state, 'monitor.dashboards.data', []), 'sequenceNumber');

// TODO: memoize this selector
export const getEnabledMonitorDashboards = state =>
  getMonitorDashboards(state).filter(dashboard => dashboard.isEnabled);

export const getMonitorDashboardsFetchingData = state =>
  get(state, 'monitor.dashboards.isFetchingDashboards');
export const getMonitorDashboardsFetchingDataError = state =>
  get(state, 'monitor.dashboards.fetchDataError');

export const getHierarchyListState = state => get(state, 'monitor.hierarchyListState');

export const getHierarchyDashboards = state => get(state, 'monitor.hierarchyDashboards');
export const getHierarchyDashboardsFetchingData = (state, resourceUUID) => {
  const hierarchyDashboardsState = getHierarchyDashboards(state);
  return (
    (hierarchyDashboardsState && hierarchyDashboardsState[resourceUUID]?.isFetchingDashboards) ||
    false
  );
};
export const getHierarchyDashboardsFetchingDataError = (state, resourceUUID) => {
  const hierarchyDashboardsState = getHierarchyDashboards(state);
  return hierarchyDashboardsState && hierarchyDashboardsState[resourceUUID]?.fetchDataError;
};

export const getAssetByIdData = (state, assetId) =>
  get(state, 'monitor.assetsData.data')?.find(asset => asset.uuid === assetId);

export const getLocationByIdData = (state, locationId) =>
  get(state, 'monitor.locations.data')?.find(location => location.uuid === locationId);
